import type {ReactNode} from 'react';
import {Info} from 'lucide-react';
import {Tooltip, TooltipContent, TooltipTrigger} from '@common-web/components/ui/tooltip';
import {cn} from '@common-web/lib/utils';

export function PipelineCountRow({
  count,
  hiddenActiveCount,
  emptyColumnCount,
  hideEmptyColumns,
  onHideEmptyColumnsChange,
  actions,
}: {
  count: number | undefined;
  // Right-aligned controls such as Customize view.
  actions?: ReactNode;
  // Board only: shown as the info hint and the empty-columns switch.
  hiddenActiveCount?: number;
  emptyColumnCount?: number;
  hideEmptyColumns?: boolean;
  onHideEmptyColumnsChange?: (hide: boolean) => void;
}) {
  return (
    <div className='flex min-h-6 flex-wrap items-center justify-between gap-3'>
      <span className='inline-flex items-center gap-1.5 text-sm text-gray-500'>
        {count !== undefined && (
          <>
            <span className='font-semibold tabular-nums text-gray-900'>
              {count.toLocaleString()}
            </span>
            {count === 1 ? 'group' : 'groups'}
          </>
        )}
        {hiddenActiveCount !== undefined && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                tabIndex={0}
                aria-label={`Active client groups hidden (${hiddenActiveCount.toLocaleString()})`}
                className='inline-flex cursor-help text-gray-500 hover:text-gray-900'
              >
                <Info className='h-3.5 w-3.5' />
              </span>
            </TooltipTrigger>
            <TooltipContent className='bg-gray-900 text-white'>
              Active client groups hidden ({hiddenActiveCount.toLocaleString()})
            </TooltipContent>
          </Tooltip>
        )}
      </span>

      <div className='ml-auto flex items-center gap-4'>
        {onHideEmptyColumnsChange && (
          <button
            type='button'
            role='switch'
            aria-checked={hideEmptyColumns}
            onClick={() => onHideEmptyColumnsChange(!hideEmptyColumns)}
            className='inline-flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-900'
          >
            <span
              className={cn(
                'relative h-[15px] w-[26px] rounded-full transition-colors',
                hideEmptyColumns ? 'bg-gray-900' : 'bg-gray-300',
              )}
            >
              <span
                className={cn(
                  'absolute top-[2px] h-[11px] w-[11px] rounded-full bg-white transition-all',
                  hideEmptyColumns ? 'left-[13px]' : 'left-[2px]',
                )}
              />
            </span>
            Hide empty columns ({emptyColumnCount ?? 0})
          </button>
        )}
        {actions}
      </div>
    </div>
  );
}
